import React, { FunctionComponent, useContext, useState } from 'react'
import { TeeRowTeeExpand } from './TeeItem.styles'
import { GrExpand } from 'react-icons/gr'
import {
  DropDownMenu,
  DropDownMenuContext,
} from '@app/ui/dropdown-menu/DropDownMenu'
import {
  AdminTitle,
  DropDownMenuItem,
} from '@app/ui/dropdown-menu/DropDownMenu.styles'
import { FormExGenderBtn } from '../screen/FormsTab/styled'
import { FaSync } from 'react-icons/fa'
import CopyTeeSubMenu from './CopyTeeSubMenu'
import CopyTeeFlow from './CopyTeeFlow'
import { Eval, EvaluationUserView, MITee } from '@app/evaluation-core/types'
import { EvalStatus } from '@app/courses/domain/ratingStatus'

// The portaled menu must sit above the evaluation card (z-index 9900, which wraps
// the tee table) so it isn't painted under the tees, but below the shared Modal
// backdrop (z-index 9999) so the copy-tee confirmation/progress modals open on top
// of the menu and stay interactive.
const TEE_MENU_Z_INDEX = 9950

type OwnProps = {
  collapsed: boolean
  evaluation: Eval
  view: EvaluationUserView
  tee: MITee
  tees: MITee[]
  getTeeLength: (teeNumber: number) => number
  collapseTee: (e: any) => void
  handleResetTee: (teeIndex: number) => void
  handleResetTeeOnAllHoles: (teeIndex: number) => void
  index: number
}

type Props = OwnProps

// Rendered as children of DropDownMenu so it can consume the close context and
// dismiss the menu after terminal actions (collapse unmounts the menu on its own,
// and selecting a copy target hands off to a modal, so neither closes here).
const TeeMenuItems: FunctionComponent<
  Pick<
    OwnProps,
    | 'evaluation'
    | 'view'
    | 'tee'
    | 'tees'
    | 'getTeeLength'
    | 'collapseTee'
    | 'handleResetTee'
    | 'handleResetTeeOnAllHoles'
  > & { onSelectCopyTarget: (tee: MITee) => void }
> = ({
  evaluation,
  view,
  tee,
  tees,
  getTeeLength,
  collapseTee,
  handleResetTee,
  handleResetTeeOnAllHoles,
  onSelectCopyTarget,
}) => {
  const { close } = useContext(DropDownMenuContext)

  return (
    <>
      <DropDownMenuItem onPress={collapseTee}>
        <FormExGenderBtn>
          <span>Collapse</span>
          <div>
            <GrExpand size={20} />
          </div>
        </FormExGenderBtn>
      </DropDownMenuItem>
      <AdminTitle>Team leader functions</AdminTitle>
      {evaluation?.status !== EvalStatus.FINALIZED && (
        <CopyTeeSubMenu
          currentTeeIndex={tee.teeIndex}
          tees={tees}
          getTeeLengthFn={getTeeLength}
          onSelectTee={onSelectCopyTarget}
        />
      )}
      <DropDownMenuItem
        onPress={() => {
          handleResetTee(tee.teeIndex)
          close()
        }}
      >
        <FormExGenderBtn>
          <span>Reset tee</span>
          <div>
            <FaSync size={20} />
          </div>
        </FormExGenderBtn>
      </DropDownMenuItem>
      <DropDownMenuItem
        onPress={() => {
          handleResetTeeOnAllHoles(tee.teeIndex)
          close()
        }}
      >
        <FormExGenderBtn>
          <span>Reset tee x {evaluation?.holes.length}</span>
          <div>
            <FaSync size={20} />
          </div>
        </FormExGenderBtn>
      </DropDownMenuItem>
    </>
  )
}

export const TeeOptionsModal: FunctionComponent<Props> = ({
  collapsed,
  evaluation,
  view,
  tees,
  tee,
  getTeeLength,
  collapseTee,
  handleResetTee,
  handleResetTeeOnAllHoles,
}) => {
  // Held here (not inside the menu) so the copy-tee modals outlive the menu
  // closing the moment a target is picked.
  const [copyTargetTee, setCopyTargetTee] = useState<MITee | null>(null)

  return (
    <>
      {collapsed ? (
        <TeeRowTeeExpand onClick={collapseTee}>
          <GrExpand />
        </TeeRowTeeExpand>
      ) : (
        <TeeRowTeeExpand>
          <DropDownMenu
            iconColor={'#000'}
            backgroundColor={'transparent'}
            value={''}
            isMenu={true}
            width={300}
            relative
            useShadow={false}
            clampToViewport
            zIndex={TEE_MENU_Z_INDEX}
          >
            <TeeMenuItems
              evaluation={evaluation}
              view={view}
              tee={tee}
              tees={tees}
              getTeeLength={getTeeLength}
              collapseTee={collapseTee}
              handleResetTee={handleResetTee}
              handleResetTeeOnAllHoles={handleResetTeeOnAllHoles}
              onSelectCopyTarget={setCopyTargetTee}
            />
          </DropDownMenu>
        </TeeRowTeeExpand>
      )}
      <CopyTeeFlow
        evaluationId={evaluation.id!}
        currentHoleIndex={view?.hole}
        currentTeeIndex={tee.teeIndex}
        tees={tees}
        selectedTee={copyTargetTee}
        onClearSelectedTee={() => setCopyTargetTee(null)}
      />
    </>
  )
}

export default TeeOptionsModal
